/**
 * @module engine3d/viewPrefs
 * @summary Per-account 3D view preferences.
 * @description
 *   The HUD toggles belong to a person, not to a drawing: which of Grid,
 *   Shadows, Labels, Tags, Floors and Dims you like on says nothing about the
 *   plant. So they are remembered per signed-in account in localStorage rather
 *   than written into the document, and they follow whoever signs in instead of
 *   being inherited from the last session on that PC.
 *
 *   Drawing-wide settings — scale, vertical exaggeration, connector routing —
 *   go the other way and live on the document. See `DocSettings`.
 */
import { KEYS, lsGet, lsSet } from '../lib/storage';
import { getCurrentUser } from '../lib/auth';

/** The toggles worth remembering. `isolate` is deliberately excluded: it is a
 *  momentary "show me just this" and would be baffling to come back to. */
export interface ViewPrefs {
  grid: boolean;
  shadows: boolean;
  labels: boolean;
  tags: boolean;
  floors: boolean;
  dims: boolean;
  dimsSelectedOnly: boolean;
  /**
   * Access decks, columns, handrails and stairs. Not one of the renderer's
   * options — it gates the steelwork at scene-build time — but it is a toggle
   * with a button like the rest, so it is remembered like the rest.
   */
  structure: boolean;
}

const DEFAULTS: ViewPrefs = {
  grid: true,
  shadows: true,
  labels: true,
  tags: true,
  floors: true,
  dims: false,
  dimsSelectedOnly: true,
  // Off until asked for. Opening a drawing in 3D should show the drawing; the
  // decks are a big opaque addition that hides the machines they carry, and
  // wanting them is a decision rather than a default.
  structure: false,
};

const keyFor = (): string => {
  const u = getCurrentUser()?.username;
  return u ? `${KEYS.view3d}::${u}` : KEYS.view3d;
};

/** This account's saved preferences, with defaults for anything missing. */
export function loadViewPrefs(): ViewPrefs {
  const raw = lsGet<Partial<ViewPrefs>>(keyFor(), {});
  const out = { ...DEFAULTS };
  for (const k of Object.keys(DEFAULTS) as (keyof ViewPrefs)[])
    if (typeof raw[k] === 'boolean') out[k] = raw[k] as boolean;
  return out;
}

/**
 * The subset that lives on the renderer's options — everything except the
 * steelwork flag, which the scene builder owns rather than the renderer.
 */
export function renderPrefs(): Omit<ViewPrefs, 'structure'> {
  const { structure: _structure, ...rest } = loadViewPrefs();
  void _structure;
  return rest;
}

/** Remember this account's preferences. Extra keys on `o` are ignored. */
export function saveViewPrefs(o: Partial<ViewPrefs>): void {
  const out: Partial<ViewPrefs> = {};
  for (const k of Object.keys(DEFAULTS) as (keyof ViewPrefs)[])
    if (typeof o[k] === 'boolean') out[k] = o[k];
  lsSet(keyFor(), out);
}
